import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { Language } from '@nova/shared';

/**
 * NFR-03/04 — Arabic by default, English available, each user picks their own.
 *
 * Namespaces are discovered from the filesystem rather than registered by hand:
 * a feature drops `locales/ar/<feature>.json` and `locales/en/<feature>.json`
 * and it is picked up. That is deliberate — with this many features editing one
 * shared translation file would be a permanent merge conflict.
 */
const modules = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/*.json', {
  eager: true,
});

const resources: Record<string, Record<string, Record<string, unknown>>> = { ar: {}, en: {} };

for (const [path, module] of Object.entries(modules)) {
  const match = path.match(/\.\/locales\/(ar|en)\/(.+)\.json$/);
  if (!match) continue;
  const [, lang, namespace] = match;
  resources[lang][namespace] = module.default;
}

export const availableNamespaces = Object.keys(resources.ar);

void i18n.use(initReactI18next).init({
  resources,
  lng: 'ar',
  fallbackLng: 'ar',
  ns: availableNamespaces.length ? availableNamespaces : ['common'],
  defaultNS: 'common',
  fallbackNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
  // A missing key should read as the key, not as an empty cell in a table.
  parseMissingKeyHandler: (key) => key.split('.').pop() ?? key,
});

/** Flip the document direction with the language (DS-50). */
export function applyLanguage(lang: 'AR' | 'EN'): void {
  const code = lang.toLowerCase();
  void i18n.changeLanguage(code);
  document.documentElement.lang = code;
  document.documentElement.dir = lang === 'AR' ? 'rtl' : 'ltr';
}

/**
 * The active language in the `'AR' | 'EN'` shape the rest of the app speaks
 * (`formatDate`, `errorMessage`, `dialCodeOptions`, the `labelAr`/`labelEn`
 * registries). Reads i18next rather than the signed-in user so it is also right
 * on the login screen, where nobody is signed in yet but the switcher still
 * works — and so the caller re-renders when the language flips.
 */
export function useLang(): Language {
  const { i18n: instance } = useTranslation();
  return instance.language === 'en' ? 'EN' : 'AR';
}

type Text = string | null | undefined;

/**
 * The one rule for bilingual data (`nameAr`/`nameEn`, `labelAr`/`labelEn`, …):
 * show the English when the UI is English and an English value exists, else the
 * Arabic. Arabic is the required column, so it is always the fallback — and the
 * other way round only covers rows whose Arabic was left blank.
 */
export function localized(lang: Language, ar: Text, en: Text): string {
  if (lang === 'EN' && en?.trim()) return en;
  if (ar?.trim()) return ar;
  return en ?? ar ?? '';
}

/** `localized` bound to the active language: `const l = useLocalized(); l(row.nameAr, row.nameEn)`. */
export function useLocalized(): (ar: Text, en: Text) => string {
  const lang = useLang();
  return (ar, en) => localized(lang, ar, en);
}

export default i18n;
